import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent, SlideshowSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();
  /** @type {{ mutationObserver: MutationObserver; intersectionObserver: IntersectionObserver } | null} */
  #autoplayObservers = null;
  /** @type {MutationObserver | null} */
  #slideObserver = null;
  #inViewport = false;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    if (this.hasAttribute('data-defer-until-visible') && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      // Observe the card, not this element: the video slide is clipped until hovered, so it never intersects
      const warmObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        warmObserver.disconnect();
        this.#warmUp();
      });
      warmObserver.observe(this.closest('product-card') ?? this);
      signal.addEventListener('abort', () => warmObserver.disconnect());
    }

    // Auto-load and play if autoplay attribute is present
    if (this.hasAttribute('autoplay')) {
      // Hide the preview button immediately when autoplay is enabled
      const previewButton = this.refs.deferredMediaPlayButton;
      if (previewButton) {
        previewButton.style.display = 'none';
      }
      
      // Find the parent slideshow-slide to watch for visibility changes
      const parentSlide = this.closest('slideshow-slide');
      const slideshow = this.closest('slideshow-component');
      
      // Listen for slideshow select events (when slide becomes active)
      if (slideshow && parentSlide) {
        const handleSlideSelect = (/** @type {SlideshowSelectEvent} */ event) => {
          // Check if this slide was selected
          if (event.detail.slide === parentSlide) {
            // Don't autoplay if the product card is in preview mode (swatch hover)
            const productCard = this.closest('product-card');
            if (productCard && productCard.hasAttribute('data-previewing-variant')) {
              return;
            }
            setTimeout(() => this.#tryAutoplay(), 200);
          }
        };
        slideshow.addEventListener(SlideshowSelectEvent.eventName, handleSlideSelect, { signal });
        
        // Also watch for aria-hidden changes as a fallback
        const slideObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
              const isVisible = parentSlide.getAttribute('aria-hidden') === 'false';
              if (isVisible) {
                // Don't autoplay if the product card is in preview mode (swatch hover)
                const productCard = this.closest('product-card');
                if (productCard && productCard.hasAttribute('data-previewing-variant')) {
                  return;
                }
                // Slide became visible, try to autoplay
                setTimeout(() => this.#tryAutoplay(), 200);
              } else if (this.hasAttribute('data-defer-until-visible')) {
                this.pauseMedia();
                const video = this.querySelector('video:not(template video)');
                if (video instanceof HTMLVideoElement) video.preload = 'metadata';
              }
            }
          });
        });
        slideObserver.observe(parentSlide, {
          attributes: true,
          attributeFilter: ['aria-hidden']
        });
        
        // Check initial state - but not during preview
        const productCard = this.closest('product-card');
        if (parentSlide.getAttribute('aria-hidden') === 'false' && 
            !(productCard && productCard.hasAttribute('data-previewing-variant'))) {
          this.#tryAutoplay();
        }
        
        // Store slide observer
        this.#slideObserver = slideObserver;
      } else {
        // Not in a slideshow, try autoplay immediately
        this.#tryAutoplay();
      }
      
      // Use MutationObserver to watch for video elements being added to DOM
      const mutationObserver = new MutationObserver(() => {
        this.#tryAutoplay();
      });
      mutationObserver.observe(this, {
        childList: true,
        subtree: true
      });
      
      // Use IntersectionObserver to play video when it becomes visible
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.#inViewport = entry.isIntersecting;
            if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
              this.#tryAutoplay();
            }
          });
        },
        { threshold: 0.1 }
      );
      intersectionObserver.observe(this);
      
      // Try immediately and with delays to catch videos that load at different times
      this.#tryAutoplay();
      setTimeout(() => this.#tryAutoplay(), 100);
      setTimeout(() => this.#tryAutoplay(), 300);
      setTimeout(() => this.#tryAutoplay(), 500);
      setTimeout(() => this.#tryAutoplay(), 1000);
      
      // Store observers for cleanup
      this.#autoplayObservers = { mutationObserver, intersectionObserver };
    }
  }

  /**
   * Attempts to autoplay the video
   */
  #tryAutoplay() {
    // Skip if already loaded and playing
    if (this.getAttribute('data-media-loaded') && this.isPlaying) {
      return;
    }

    // Product cards keep the video in a template; only fetch it once its slide is actually on screen
    if (
      this.hasAttribute('data-defer-until-visible') &&
      (!this.#inViewport || this.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'true')
    ) {
      return;
    }
    
    // Don't autoplay if the product card is in preview mode (swatch hover)
    const productCard = this.closest('product-card');
    if (productCard && productCard.hasAttribute('data-previewing-variant')) {
      return;
    }
    
    // Check if video is already rendered directly (not in template)
    const existingVideo = this.querySelector('video:not(template video)');
    const existingIframe = this.querySelector('iframe:not(template iframe)');
    
    if (existingVideo instanceof HTMLVideoElement) {
      // Video is already rendered directly
      this.setAttribute('data-media-loaded', 'true');
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
      
      // Ensure video has autoplay and muted attributes
      existingVideo.setAttribute('autoplay', '');
      existingVideo.setAttribute('muted', '');
      existingVideo.setAttribute('playsinline', '');
      
      // Explicitly play the video (required for some browsers)
      const playPromise = existingVideo.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.isPlaying = true;
            this.updatePlayPauseHint(this.isPlaying);
          })
          .catch((error) => {
            // Autoplay was prevented, user interaction required
            console.log('Video autoplay was prevented:', error);
          });
      }
    } else if (existingIframe instanceof HTMLIFrameElement) {
      // Iframe is already rendered directly (YouTube/Vimeo)
      this.setAttribute('data-media-loaded', 'true');
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
      
      // For iframes, try to ensure playback after a short delay to let iframe load
      setTimeout(() => {
        if (existingIframe.dataset.videoType === 'youtube') {
          existingIframe.contentWindow?.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            '*'
          );
        } else if (existingIframe.dataset.videoType === 'vimeo') {
          existingIframe.contentWindow?.postMessage('{"method":"play"}', '*');
        }
      }, 500);
    } else {
      // Video is in template, load it immediately and play
      if (!this.getAttribute('data-media-loaded')) {
        this.loadContent(false);
        
        // After loading, ensure video plays
        setTimeout(() => {
          const loadedVideo = this.querySelector('video');
          const loadedIframe = this.querySelector('iframe');
          
          if (loadedVideo instanceof HTMLVideoElement) {
            // Hide the preview button
            this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
            
            // Ensure video has autoplay and muted attributes
            loadedVideo.setAttribute('autoplay', '');
            loadedVideo.setAttribute('muted', '');
            loadedVideo.setAttribute('playsinline', '');
            
            // Play the video
            const playPromise = loadedVideo.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  this.isPlaying = true;
                  this.updatePlayPauseHint(this.isPlaying);
                })
                .catch((error) => {
                  console.log('Video autoplay was prevented:', error);
                });
            }
          } else if (loadedIframe instanceof HTMLIFrameElement) {
            // Hide the preview button
            this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
            this.isPlaying = true;
            this.updatePlayPauseHint(this.isPlaying);
            
            // For iframes, autoplay is handled via URL parameters
            // But we can trigger play if needed
            setTimeout(() => {
              if (loadedIframe.dataset.videoType === 'youtube') {
                loadedIframe.contentWindow?.postMessage(
                  '{"event":"command","func":"playVideo","args":""}',
                  '*'
                );
              } else if (loadedIframe.dataset.videoType === 'vimeo') {
                loadedIframe.contentWindow?.postMessage('{"method":"play"}', '*');
              }
            }, 500);
          }
        }, 150);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    
    // Clean up autoplay observers
    if (this.#autoplayObservers) {
      this.#autoplayObservers.mutationObserver?.disconnect();
      this.#autoplayObservers.intersectionObserver?.disconnect();
    }
    
    // Clean up slide observer
    this.#slideObserver?.disconnect();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Sets data-video-ready once the media has actually painted, so CSS can swap the preview image for it.
   * @param {Element} content
   */
  #markReadyOnFirstFrame(content) {
    const markReady = () => this.setAttribute('data-video-ready', '');
    if (content instanceof HTMLVideoElement && 'requestVideoFrameCallback' in content) {
      // "playing" fires before a frame is painted (notably with HLS); swapping then shows a blank card
      content.requestVideoFrameCallback(markReady);
    } else if (content instanceof HTMLVideoElement) {
      const onTime = () => {
        if (content.currentTime > 0) {
          content.removeEventListener('timeupdate', onTime);
          markReady();
        }
      };
      content.addEventListener('timeupdate', onTime);
    } else {
      content.addEventListener('load', markReady, { once: true });
    }
  }

  /**
   * Hover-capable desktops only: attach the card video paused with preload="metadata" once the card is on screen,
   * so a hover starts playback without waiting for the network.
   */
  #warmUp() {
    if (this.getAttribute('data-media-loaded')) return;
    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!(content instanceof HTMLVideoElement)) return;

    // Native HLS ignores preload="metadata" and buffers whole streams; the mp4 honours it
    const mp4Source = content.querySelector('source[type="video/mp4"]');
    if (mp4Source) content.querySelectorAll('source:not([type="video/mp4"])').forEach((source) => source.remove());
    content.removeAttribute('autoplay');
    content.preload = 'metadata';
    // "metadata" usually stops at ~2.7 s, but some files stop under 1 s; resuming from the video CDN can take
    // over a second, so top those up to MIN_BUFFER_SECONDS and stop again
    // "progress" fires only every ~350 ms, long enough for "auto" to pull several MB and keep the connection busy,
    // which starves the request of the card being hovered; so poll while topping up
    const MIN_BUFFER_SECONDS = 2;
    const TOP_UP_POLL_MS = 100;
    const TOP_UP_TIMEOUT_MS = 5000;
    const bufferedAhead = () => (content.buffered.length ? content.buffered.end(0) : 0);
    const topUp = () => {
      if (content.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || bufferedAhead() >= MIN_BUFFER_SECONDS) return;
      content.removeEventListener('suspend', topUp);
      content.preload = 'auto';
      const startedAt = performance.now();
      const poll = setInterval(() => {
        const done = bufferedAhead() >= MIN_BUFFER_SECONDS || performance.now() - startedAt > TOP_UP_TIMEOUT_MS;
        if (!content.paused) {
          clearInterval(poll);
        } else if (done) {
          clearInterval(poll);
          content.preload = 'metadata';
        }
      }, TOP_UP_POLL_MS);
    };
    content.addEventListener('suspend', topUp);
    this.setAttribute('data-media-loaded', 'true');
    this.#markReadyOnFirstFrame(content);
    this.appendChild(content);
  }

  /**
   * Card hover preview: starts the video while its slide is still hidden and resolves once playback has visibly
   * moved, so the card can swap straight to a moving video instead of showing a frozen first frame while Chrome
   * resumes an idle player (a few hundred ms).
   * @param {AbortSignal} signal - Aborted when the pointer leaves; pauses the video.
   * @returns {Promise<boolean>} Whether playback moved before the signal was aborted.
   */
  previewWhenMoving(signal) {
    this.#warmUp();
    const video = this.querySelector('video:not(template video)');
    if (!(video instanceof HTMLVideoElement)) return Promise.resolve(false);

    return new Promise((resolve) => {
      const done = (/** @type {boolean} */ moved) => {
        video.removeEventListener('timeupdate', onTime);
        signal.removeEventListener('abort', onAbort);
        resolve(moved);
      };
      const onAbort = () => {
        video.pause();
        // Otherwise the paused video keeps downloading and competes with the next hovered card
        video.preload = 'metadata';
        done(false);
      };
      const onTime = () => {
        if (video.currentTime > 0.05) done(true);
      };
      if (signal.aborted) return onAbort();
      signal.addEventListener('abort', onAbort);
      video.addEventListener('timeupdate', onTime);
      if ('requestVideoFrameCallback' in video) {
        const onFrame = (/** @type {number} */ _now, /** @type {{ mediaTime: number }} */ meta) => {
          if (signal.aborted) return;
          if (meta.mediaTime > 0.05) done(true);
          else video.requestVideoFrameCallback(onFrame);
        };
        video.requestVideoFrameCallback(onFrame);
      }
      video.preload = 'auto';
      video.play().catch(() => done(false));
    });
  }

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    if (this.hasAttribute('data-defer-until-visible')) this.#markReadyOnFirstFrame(content);
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    } else if (content instanceof HTMLIFrameElement) {
      // For iframes (YouTube/Vimeo), autoplay is handled via URL parameters
      // but we need to ensure it starts playing
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
